/**
 * Fetch an auth-gated image (e.g., `/api/v1/files/...`) with the active
 * Bearer token and return a blob URL the browser can use as an `<img src>`.
 *
 * Why this exists: browsers don't send the `Authorization` header on bare
 * `<img>` / `<video>` / `<audio>` requests, only on `fetch()` /
 * `XMLHttpRequest`. So we fetch the bytes ourselves, wrap them in a blob,
 * and hand back an `objectURL` — the same pattern `LakeDocumentPreview`
 * uses for PDF / audio / image previews.
 *
 * Returns `null` until the fetch completes; `null` also on error or when
 * `url` is null/undefined. The object URL is revoked on unmount and when
 * the source URL changes so we never leak memory.
 *
 * Pass an absolute API path (`/api/v1/files/...`) or a same-origin URL.
 * Returns the input unchanged when it's already a blob: / data: URL so
 * callers can pass through user-uploaded previews — and images the API
 * sends inline, like a capture's thumbnail — without a fetch: those are
 * returned in the same render, so nothing waits for an effect.
 */
import { useEffect, useState } from 'react'
import { apiClient } from '@/services/api/client'

/**
 * Whether an `<img>` draws the URL as it is: a local preview (blob:) or an
 * inline image (data:). Anything else sits behind the API and is fetched.
 */
export function isDirectImageUrl(url: string): boolean {
  return url.startsWith('blob:') || url.startsWith('data:')
}

export function useAuthedImageUrl(url: string | null | undefined): string | null {
  const [resolved, setResolved] = useState<string | null>(null)

  useEffect(() => {
    if (!url) {
      setResolved(null)
      return
    }
    // Blob / data URLs are already directly renderable — short-circuit so
    // callers can pass them through without an extra round-trip.
    if (isDirectImageUrl(url)) {
      setResolved(url)
      return
    }

    let objectUrl: string | null = null
    let cancelled = false

    const load = async () => {
      try {
        const token = apiClient.getToken()
        const response = await fetch(url, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!response.ok) {
          if (!cancelled) setResolved(null)
          return
        }
        const blob = await response.blob()
        objectUrl = URL.createObjectURL(blob)
        if (cancelled) {
          URL.revokeObjectURL(objectUrl)
          return
        }
        setResolved(objectUrl)
      } catch {
        if (!cancelled) setResolved(null)
      }
    }

    void load()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url])

  return url && isDirectImageUrl(url) ? url : resolved
}
